import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import { useMemo } from 'react';

import { notifications } from '@mantine/notifications';

import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import useApi from '@/hooks/useApi/useApi';
import { queryKeys } from '@/lib/queryKeys';
import { track } from '@/modules/Analytics';
import { Project, Task, TaskDependency } from '@/types/types';

type TaskDependencyApiError = {
  error?: string;
  errors?: string[];
};

const extractErrorMessage = (error: AxiosError<TaskDependencyApiError>, fallback: string) =>
  error.response?.data?.error || error.response?.data?.errors?.[0] || fallback;

// Fetch a task's predecessors ("waiting on" — this task is the successor)
export const useTaskDependencies = (taskId: Task['id']) => {
  const { axiosInstance } = useApi();

  return useQuery({
    queryKey: queryKeys.tasks.dependencies(taskId).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<TaskDependency[]>(
        `/api/v1/tasks/${taskId}/dependencies`
      );
      return data;
    },
    enabled: !!taskId,
  });
};

// Fan out project dependency fetches across multiple projects at once (the
// Gantt shows many projects side by side, so a single-project fetch isn't enough).
export const useDependenciesForProjects = (projectIds: Project['id'][]) => {
  const { axiosInstance } = useApi();

  const results = useQueries({
    queries: projectIds.map((projectId) => ({
      queryKey: queryKeys.projects.dependencies(projectId).queryKey,
      queryFn: async () => {
        const { data } = await axiosInstance.get<TaskDependency[]>(
          `/api/v1/projects/${projectId}/dependencies`
        );
        return data;
      },
    })),
  });

  return useMemo(
    () => ({
      data: results.flatMap((result) => result.data ?? []),
      isLoading: results.some((result) => result.isLoading),
    }),
    [results]
  );
};

// Add a dependency link without being bound to a fixed successor task (e.g. from the Gantt,
// where the successor is whichever bar the link is dropped on).
export const useCreateDependencyLink = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation('task');

  return useMutation({
    mutationFn: async (link: {
      predecessorTaskId: number;
      successorTaskId: number;
      projectId: Project['id'];
      lagDays?: number;
    }) => {
      const { data } = await axiosInstance.post<TaskDependency>(
        `/api/v1/tasks/${link.successorTaskId}/dependencies`,
        {
          task_dependency: {
            predecessor_task_id: link.predecessorTaskId,
            lag_days: link.lagDays ?? 0,
          },
        }
      );
      return { ...data, projectId: link.projectId };
    },
    onSuccess: ({ projectId, successor_task_id }) => {
      track('dependency_added');
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.dependencies(projectId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.dependencies(successor_task_id).queryKey,
      });
      // The dependency graph feeds CriticalPathService server-side — slack/
      // is_critical on every task in the project can shift, not just the two
      // tasks in this link, so the whole planning cache needs a refetch.
      queryClient.invalidateQueries({ queryKey: queryKeys.planningProjects._def });
    },
    onError: (error: AxiosError<TaskDependencyApiError>) => {
      notifications.show({
        title: t('dependencies.error_title'),
        message: extractErrorMessage(error, t('dependencies.create_failed')),
        color: 'red',
      });
    },
  });
};

// Update a dependency link's lag_days and/or note without being bound to a fixed successor task.
export const useUpdateDependencyLink = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation('task');

  return useMutation({
    mutationFn: async (link: {
      dependencyId: TaskDependency['id'];
      successorTaskId: number;
      projectId: Project['id'];
      lagDays?: number;
      note?: string;
    }) => {
      const { data } = await axiosInstance.patch<TaskDependency>(
        `/api/v1/tasks/${link.successorTaskId}/dependencies/${link.dependencyId}`,
        {
          task_dependency: {
            ...(link.lagDays !== undefined && { lag_days: link.lagDays }),
            ...(link.note !== undefined && { note: link.note }),
          },
        }
      );
      return { ...data, projectId: link.projectId };
    },
    onSuccess: ({ projectId, successor_task_id }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.dependencies(projectId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.dependencies(successor_task_id).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.planningProjects._def });
    },
    onError: (error: AxiosError<TaskDependencyApiError>) => {
      notifications.show({
        title: t('dependencies.error_title'),
        message: extractErrorMessage(error, t('dependencies.update_failed')),
        color: 'red',
      });
    },
  });
};

// Remove a dependency link without being bound to a fixed successor task.
export const useDeleteDependencyLink = () => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation('task');

  return useMutation({
    mutationFn: async (link: {
      dependencyId: TaskDependency['id'];
      successorTaskId: number;
      projectId: Project['id'];
    }) => {
      await axiosInstance.delete(
        `/api/v1/tasks/${link.successorTaskId}/dependencies/${link.dependencyId}`
      );
      return link;
    },
    onSuccess: ({ projectId, successorTaskId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.dependencies(projectId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.dependencies(successorTaskId).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.planningProjects._def });
    },
    onError: (error: AxiosError<TaskDependencyApiError>) => {
      notifications.show({
        title: t('dependencies.error_title'),
        message: extractErrorMessage(error, t('dependencies.delete_failed')),
        color: 'red',
      });
    },
  });
};

// Add a task this task is waiting on
export const useCreateTaskDependency = (taskId: Task['id'], projectId?: Project['id'] | null) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation('task');

  return useMutation({
    mutationFn: async (dependency: {
      predecessor_task_id: number;
      lag_days?: number;
      note?: string;
    }) => {
      const { data } = await axiosInstance.post<TaskDependency>(
        `/api/v1/tasks/${taskId}/dependencies`,
        { task_dependency: dependency }
      );
      return data;
    },
    onSuccess: () => {
      track('dependency_added');
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.dependencies(taskId).queryKey,
      });
      // Keep the Gantt's project-scoped dependency cache (drawn as arrows) in
      // sync too — it's a separate cache entry from the per-task one above.
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.dependencies(projectId).queryKey,
        });
      }
      // The dependency graph feeds CriticalPathService server-side — refetch
      // the planning cache so slack/is_critical stay in sync project-wide.
      queryClient.invalidateQueries({ queryKey: queryKeys.planningProjects._def });
    },
    onError: (error: AxiosError<TaskDependencyApiError>) => {
      notifications.show({
        title: t('dependencies.error_title'),
        message: extractErrorMessage(error, t('dependencies.create_failed')),
        color: 'red',
      });
    },
  });
};

// Remove a "waiting on" link from this task
export const useDeleteTaskDependency = (taskId: Task['id'], projectId?: Project['id'] | null) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation('task');

  return useMutation({
    mutationFn: async (dependencyId: TaskDependency['id']) => {
      await axiosInstance.delete(`/api/v1/tasks/${taskId}/dependencies/${dependencyId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tasks.dependencies(taskId).queryKey,
      });
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.dependencies(projectId).queryKey,
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.planningProjects._def });
    },
    onError: (error: AxiosError<TaskDependencyApiError>) => {
      notifications.show({
        title: t('dependencies.error_title'),
        message: extractErrorMessage(error, t('dependencies.delete_failed')),
        color: 'red',
      });
    },
  });
};
